import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "./InputField";
import SpeakerField from "./SpeakerField";
import FileUpload from "./FileUpload";
import TagOptions from "./TagOptions";
import SearchableSelect from "../../ui/SearchableSelect";
import { transcriptApi } from "../../../api/transcriptApi";
import { wpApi } from "../../../api/wpApi";
import { faiconApi } from "../../../api/faiconApi";
import { extractUniqueDatesWithEventIDs, extractWordPressChildEventDates, extractFaiconSessionDates } from "../../../services/DateExtractor";
import { extractUniqueDays, htmlToText } from "../../../services/FaiconHelpers";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { fileApi } from "../../../api/fileApi";
import Modal from "../../ui/Modal";
import Notification from "../../ui/Notification";
import { 
  CloudArrowUpIcon, 
  DocumentTextIcon, 
  UserGroupIcon,
  TagIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  XMarkIcon,
  CheckCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { TRANSCRIPT_PROVIDERS, DEFAULT_PROVIDER } from '../../../constants/transcriptProviders';

const FAICON_API_KEY = process.env.REACT_APP_FAICON_APP_TOKEN;
const FAICON_API_URL = process.env.REACT_APP_FAICON_APP_URL;
const FAICON_API_EXISTS = FAICON_API_KEY && FAICON_API_URL;

export default function TranscriptUploader() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [createdSessionId, setCreatedSessionId] = useState(null); // Track created session
  const formDataInitialState = useMemo(
    () => ({
      user_id: "",
      file_id: "",
      file_type: "",
      event_name: "",
      event_day: "",
      event_id: "",
      title: "",
      tags: [],
      wp_id: "",
      upload_date: "",
      description: "",
      moderator: "",
      speakers: [],
      faicon_session_id: "",
      transcript_status: "",
      transcript: {},
      report_status: "",
      report: {},
      report_url: "",
      auto_generate_transcript: true,
      transcript_provider: DEFAULT_PROVIDER,
    }),
    []
  );
  const [formData, setFormData] = useState(formDataInitialState);
  const user_id = "123";
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [typeOfTitle, setTypeOfTitle] = useState("WordPress");
  const [speakers, setSpeakers] = useState([""]);
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_PROVIDER);

  // Use the new file upload hook
  const { uploadFile, uploading, progress, uploadedFile, error: uploadError, resetUpload } = useFileUpload();

  // Reset state function
  const resetState = useCallback(() => {
    setDays([]);
    setSessions([]);
    setSelectedEvent(null);
    setSelectedDay(null);
    setSpeakers([""]);
    setFormData(formDataInitialState);
    setSelectedSource(null);
    setShowForm(false);
    setIsLoadingEvents(false);
    setSelectedProvider(DEFAULT_PROVIDER);
    setCreatedSessionId(null); // Clear created session ID
  }, [formDataInitialState]);

  // Set user_id, uploaded date and initial status in formData
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      user_id: user_id,
    }));
  }, [user_id]);

  // Handle source selection
  const handleSourceSelection = async (source) => {
    
    // Reset form and hide it when changing source
    if (selectedSource && selectedSource !== source) {
      resetState();
      setShowForm(false);
    }
    
    setSelectedSource(source);
    setTypeOfTitle(source);
    
    // If Custom is selected, show form immediately
    if (source === "Custom") {
      setShowForm(true);
      setIsLoadingEvents(false);
      return;
    }
    
    // For WordPress and Faicon, show loading and fetch events
    const startTime = Date.now();
    setIsLoadingEvents(true);
    setShowForm(false); // Hide form while loading
    
    try {
      let eventsData;
      if (source === "WordPress") {
        const wpEvents = await wpApi.getEventsWithChildren();
        eventsData = wpEvents.map(event => ({
          id: event.ID,
          title: event.post_title,
          slug: event.post_name,
          children: event.children || []
        }));
      } else if (source === "Faicon") {
        const faiconResponse = await faiconApi.getAllEvents();
        console.log('Faicon API response:', faiconResponse);
        
        // Check if response has data or results property, or if it's directly an array
        let faiconEvents;
        if (faiconResponse && Array.isArray(faiconResponse)) {
          faiconEvents = faiconResponse;
        } else if (faiconResponse && Array.isArray(faiconResponse.data)) {
          faiconEvents = faiconResponse.data;
        } else if (faiconResponse && Array.isArray(faiconResponse.results)) {
          faiconEvents = faiconResponse.results;
        } else {
          console.error('Faicon API response is not an array:', faiconResponse);
          throw new Error('Invalid response format from Faicon API');
        }
        
        eventsData = faiconEvents.map(event => ({
          id: event.id,
          title: event.name || event.title,
          sessions: event.sessions || []
        }));
      }
      
      const loadingTime = Date.now() - startTime;
      
      setEvents(eventsData || []);
      
      // Show form immediately after loading completes
      setShowForm(true);
      setIsLoadingEvents(false);
      
      
    } catch (error) {
      const loadingTime = Date.now() - startTime;
      setNotification({
        type: 'error',
        title: 'Loading Failed',
        message: `Failed to load events from ${source} after ${loadingTime}ms. Please try again.`
      });
      setIsLoadingEvents(false);
      setSelectedSource(null);
      setShowForm(false);
    }
  };

  // Get days from selected event
  useEffect(() => {
    if (selectedEvent && typeOfTitle !== "Custom") {
      const fetchDays = async () => {
        try {
          let daysData;
          if (typeOfTitle === "WordPress") {
            // For WordPress, we need to fetch child events and group by day
            const childEvents = await wpApi.getChildEvents(selectedEvent.id);
            
            // Use the new WordPress-specific function
            daysData = extractWordPressChildEventDates(childEvents);
          } else {
            // For Faicon, fetch sessions for the event and then extract days
            const faiconResponse = await faiconApi.getSessionsByEvent(selectedEvent.id);
            
            // Extract sessions from the response (Faicon returns { count, next, previous, results: [...] })
            const faiconSessions = faiconResponse?.results || faiconResponse || [];
            
            // Use the new Faicon-specific function
            daysData = extractFaiconSessionDates(faiconSessions);
          }
          setDays(daysData || []);
        } catch (error) {
          console.error('Error extracting days:', error);
          setDays([]);
        }
      };
      fetchDays();
    } else {
      setDays([]);
    }
  }, [selectedEvent, typeOfTitle]);

  // Get sessions from selected day
  useEffect(() => {
    if (selectedDay && typeOfTitle !== "Custom") {
      const fetchSessions = async () => {
        try {
          let sessionsData;
          if (typeOfTitle === "WordPress") {
            // For WordPress, use sessions already stored in selectedDay
            sessionsData = selectedDay.sessions || [];
          } else {
            // For Faicon, use sessions already stored in selectedDay
            sessionsData = selectedDay.sessions || [];
          }
          setSessions(sessionsData || []);
        } catch (error) {
          console.error('Error fetching sessions:', error);
          setSessions([]);
        }
      };
      fetchSessions();
    } else {
      setSessions([]);
    }
  }, [selectedDay, typeOfTitle]);

  // Handle file upload
  const handleFileUpload = async (uploadedFile) => {
    setFile(uploadedFile);
    setFormData((prevData) => ({
      ...prevData,
      file_type: uploadedFile.type,
    }));
    
    // Automatically start upload when file is selected
    try {
      const uploadResult = await uploadFile(uploadedFile);
      console.log('Upload result:', uploadResult);
      
      if (uploadResult && uploadResult.id) {
        console.log('Setting uploadedFileId to:', uploadResult.id);
        setUploadedFileId(uploadResult.id);
        setIsFileUploaded(true);
        
        // Update form data with file information
        setFormData((prevData) => ({
          ...prevData,
          file_id: uploadResult.id,
          file_type: uploadResult.mime_type,
        }));
        
        setNotification({
          type: 'success',
          title: 'Upload Successful!',
          message: 'File uploaded successfully! You can now fill out the form and submit.'
        });
      } else {
        console.log('No file_id in upload result:', uploadResult);
      }
    } catch (error) {
      console.error('File upload failed:', error);
      setNotification({
        type: 'error',
        title: 'Upload Failed',
        message: 'File upload failed. Please try again.'
      });
      setFile(null);
      setUploadedFileId(null);
      setIsFileUploaded(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFileUploaded || !uploadedFileId) {
      setNotification({
        type: 'error',
        title: 'Form Submission Error',
        message: 'Please upload a file first before submitting the form.'
      });
      return;
    }

    try {
      // Prepare session data according to API specification
      const sessionData = {
        upload_id: uploadedFileId,
        title: formData.title,
        description: formData.description,
        auto_generate_transcript: formData.auto_generate_transcript,
      };

      // Include transcript provider if auto-generate is enabled
      if (formData.auto_generate_transcript) {
        sessionData.transcript_provider = selectedProvider;
      }

      // Include exactly one of: wp_id, faicon_id, or event_data
      if (selectedSource === "WordPress" && formData.wp_id) {
        sessionData.wp_id = String(formData.wp_id);
        // Include WordPress session/page ID if selected
        if (formData.faicon_session_id) {
          sessionData.wp_session_id = String(formData.faicon_session_id);
        }
        // Also send event_data with the actual event name for proper event naming
        sessionData.event_data = { title: selectedEvent?.title || formData.title };
        if (formData.description && formData.description.trim() !== "") {
          sessionData.event_data.description = formData.description;
        }
      } else if (selectedSource === "Faicon" && formData.faicon_id) {
        sessionData.faicon_id = String(formData.faicon_id);
        // Include FAICON session ID if selected
        if (formData.faicon_session_id) {
          sessionData.faicon_session_id = String(formData.faicon_session_id);
        }
        // Also send event_data with the actual event name for proper event naming
        sessionData.event_data = { title: selectedEvent?.title || formData.title };
        if (formData.description && formData.description.trim() !== "") {
          sessionData.event_data.description = formData.description;
        }
      } else {
        sessionData.event_data = { title: selectedEvent?.title || formData.title };
        if (formData.description && formData.description.trim() !== "") {
          sessionData.event_data.description = formData.description;
        }
      }

      console.log('Submitting session data:', sessionData);

      // Create session record
      const sessionResult = await transcriptApi.createSession(sessionData);
      
      if (sessionResult) {
        // Store created session ID for queue monitoring
        setCreatedSessionId(sessionResult.id);
        
        // Show notification with queue information if available
        let message = 'Session created successfully!';
        if (sessionResult.queue_position !== null && sessionResult.queue_position !== undefined) {
          message += ` Added to queue at position #${sessionResult.queue_position}.`;
          if (sessionResult.estimated_wait_time) {
            const mins = Math.floor(sessionResult.estimated_wait_time / 60);
            const secs = sessionResult.estimated_wait_time % 60;
            if (mins === 0) {
              message += ` Estimated wait time: ${secs}sec`;
            } else if (secs === 0) {
              message += ` Estimated wait time: ${mins}min`;
            } else {
              message += ` Estimated wait time: ${mins}min i ${secs}sec`;
            }
          }
        }
        
        setNotification({
          type: 'success',
          title: 'Session Created!',
          message: message
        });

        // Redirect to sessions list after successful creation
        navigate('/sessions');
        
        // Reset form and upload state (but keep createdSessionId for monitoring)
        setFile(null);
        setUploadedFileId(null);
        setIsFileUploaded(false);
        setFormData(formDataInitialState);
        resetUpload();
        // Don't reset state completely - we want to show queue status
        setShowForm(false);
      } else {
        setNotification({
          type: 'warning',
          title: 'Session Creation Warning',
          message: 'Session creation failed'
        });
      }
    } catch (error) {
      console.error('Session creation error:', error);
      setNotification({
        type: 'error',
        title: 'Session Creation Error',
        message: error.message || 'Session creation failed. Please try again.'
      });
    }
  };

  // Handle input changes
  const handleInputChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle speaker changes
  const handleSpeakerChange = (newSpeakers) => {
    setSpeakers(newSpeakers);
    setFormData((prevData) => ({
      ...prevData,
      speakers: newSpeakers.filter((speaker) => speaker.trim() !== ""),
    }));
  };

  // Handle tag changes
  const handleTagChange = (tags) => {
    setFormData((prevData) => ({
      ...prevData,
      tags,
    }));
  };

  // Handle file deletion
  const handleDeleteFile = async () => {
    console.log('handleDeleteFile called with uploadedFileId:', uploadedFileId);
    
    if (!uploadedFileId) {
      console.log('No uploadedFileId, returning early');
      return;
    }
    
    setIsDeleting(true);
    try {
      console.log('Calling fileApi.deleteFile with ID:', uploadedFileId);
      
      // Call the backend delete API
      const result = await fileApi.deleteFile(uploadedFileId);
      console.log('Delete API response:', result);
      
      setNotification({
        type: 'success',
        title: 'File Deleted',
        message: 'File has been deleted successfully. You can now upload a new file.'
      });
      
      // Reset all states
      setFile(null);
      setUploadedFileId(null);
      setIsFileUploaded(false);
      setFormData(formDataInitialState);
      resetUpload();
      // Don't call resetState here as it resets showDeleteModal
      // resetState();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('File deletion error:', error);
      setNotification({
        type: 'error',
        title: 'Deletion Failed',
        message: `Failed to delete file: ${error.message || 'Unknown error'}. Please try again.`
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if form can be submitted
  const canSubmit = formData.title && formData.title.trim() !== "" && file;



  return (
    <div className="space-y-6">
      {/* Content Source Selection */}
      <div className="bg-white rounded-lg p-6 border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <AcademicCapIcon className="h-5 w-5 mr-2 text-blue-600" />
          Content Source
        </h3>
        <p className="text-gray-600 mb-6">Choose where your content comes from to automatically populate event information.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* WordPress Option */}
          <div 
            className={`
              relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105
              ${selectedSource === "WordPress" 
                ? "border-blue-500 bg-blue-50 shadow-lg" 
                : "border-gray-200 hover:border-blue-300 bg-white hover:shadow-md"
              }
            `}
            onClick={() => handleSourceSelection("WordPress")}
          >
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                selectedSource === "WordPress" ? "bg-blue-100" : "bg-gray-100"
              }`}>
                <AcademicCapIcon className={`h-8 w-8 ${
                  selectedSource === "WordPress" ? "text-blue-600" : "text-gray-600"
                }`} />
              </div>
              <h4 className={`text-lg font-semibold mb-2 ${
                selectedSource === "WordPress" ? "text-blue-700" : "text-gray-700"
              }`}>
                WordPress
              </h4>
              <p className={`text-sm ${
                selectedSource === "WordPress" ? "text-blue-600" : "text-gray-500"
              }`}>
                Import from WordPress events
              </p>
            </div>
            {selectedSource === "WordPress" && (
              <div className="absolute top-3 right-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              </div>
            )}
          </div>

          {/* Faicon Option */}
          <div 
            className={`
              relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105
              ${selectedSource === "Faicon" 
                ? "border-green-500 bg-green-50 shadow-lg" 
                : "border-gray-200 hover:border-green-300 bg-white hover:shadow-md"
              }
            `}
            onClick={() => handleSourceSelection("Faicon")}
          >
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                selectedSource === "Faicon" ? "bg-green-100" : "bg-gray-100"
              }`}>
                <AcademicCapIcon className={`h-8 w-8 ${
                  selectedSource === "Faicon" ? "text-green-600" : "text-gray-600"
                }`} />
              </div>
              <h4 className={`text-lg font-semibold mb-2 ${
                selectedSource === "Faicon" ? "text-green-700" : "text-gray-700"
              }`}>
                Faicon
              </h4>
              <p className={`text-sm ${
                selectedSource === "Faicon" ? "text-green-600" : "text-gray-500"
              }`}>
                Import from Faicon events
              </p>
            </div>
            {selectedSource === "Faicon" && (
              <div className="absolute top-3 right-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
            )}
          </div>

          {/* Custom Option */}
          <div 
            className={`
              relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105
              ${selectedSource === "Custom" 
                ? "border-purple-500 bg-purple-50 shadow-lg" 
                : "border-gray-200 hover:border-purple-300 bg-white hover:shadow-md"
              }
            `}
            onClick={() => handleSourceSelection("Custom")}
          >
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                selectedSource === "Custom" ? "bg-purple-100" : "bg-gray-100"
              }`}>
                <AcademicCapIcon className={`h-8 w-8 ${
                  selectedSource === "Custom" ? "text-purple-600" : "text-gray-600"
                }`} />
              </div>
              <h4 className={`text-lg font-semibold mb-2 ${
                selectedSource === "Custom" ? "text-purple-700" : "text-gray-700"
              }`}>
                Custom
              </h4>
              <p className={`text-sm ${
                selectedSource === "Custom" ? "text-purple-600" : "text-gray-500"
              }`}>
                Manual entry
              </p>
            </div>
            {selectedSource === "Custom" && (
              <div className="absolute top-3 right-3">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form - Show when source is selected */}
      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Event Selection for WordPress and Faicon */}
          {selectedSource && selectedSource !== "Custom" && (
            <div className="bg-white rounded-lg p-6 border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CalendarDaysIcon className="h-5 w-5 mr-2 text-blue-600" />
                Event Selection
              </h3>
              
              {/* Loading State */}
              {isLoadingEvents && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading events from {selectedSource}...</p>
                </div>
              )}

              {/* Event Selection */}
              {!isLoadingEvents && events.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event *
                  </label>
                  <SearchableSelect
                    options={events}
                    value={selectedEvent}
                    onChange={(event) => {
                      setSelectedEvent(event);
                      // Set wp_id or faicon_id based on selected source
                      if (selectedSource === "WordPress") {
                        setFormData(prev => ({
                          ...prev,
                          wp_id: event?.id || "",
                          faicon_id: ""
                        }));
                      } else if (selectedSource === "Faicon") {
                        setFormData(prev => ({
                          ...prev,
                          faicon_id: event?.id || "",
                          wp_id: ""
                        }));
                      }
                    }}
                    placeholder="Select an event..."
                    optionLabel="title"
                    optionValue="id"
                  />
                </div>
              )}

              {/* Day Selection */}
              {selectedEvent && days.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Day *
                  </label>
                  <SearchableSelect
                    options={days}
                    value={selectedDay}
                    onChange={setSelectedDay}
                    placeholder="Select a day..."
                    optionLabel="date"
                    optionValue="event_id"
                  />
                </div>
              )}

              {/* Session Selection */}
              {selectedDay && sessions.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session *
                  </label>
                  <SearchableSelect
                    options={sessions}
                    value={sessions.find(s => s.ID === formData.faicon_session_id || s.id === formData.faicon_session_id)}
                    onChange={(session) => {
                      setFormData(prev => ({
                        ...prev,
                        faicon_session_id: session?.ID || session?.id || "",
                        title: session?.post_title || session?.title || session?.name || ""
                      }));
                    }}
                    placeholder="Select a session..."
                    optionLabel={typeOfTitle === "WordPress" ? "post_title" : "name"}
                    optionValue={typeOfTitle === "WordPress" ? "ID" : "id"}
                  />
                </div>
              )}
            </div>
          )}

          {/* Form Fields */}
          <div className="bg-white rounded-lg p-6 border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
              Transcript Details
            </h3>
            <p className="text-gray-600 mb-6">Fill in the details for your transcript.</p>
            
            {/* Title Field */}
            <div className="mb-6">
              <InputField
                label="Title *"
                name="title"
                value={formData.title}
                onChange={(value) => setFormData(prev => ({ ...prev, title: value }))}
                placeholder="Enter transcript title"
                required
              />
            </div>

            {/* Description Field */}
            <div className="mb-6">
              <InputField
                label="Description"
                name="description"
                value={formData.description}
                onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                placeholder="Enter transcript description"
              />
            </div>

            {/* Tags Field */}
            <div className="mb-6">
              <TagOptions
                selectedTags={formData.tags}
                onTagsChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                availableTags={[
                  "Diplomacy", "International Relations", "Policy", "Security",
                  "Economics", "Human Rights", "Environment", "Technology",
                  "Education", "Health", "Migration", "Conflict Resolution"
                ]}
              />
            </div>

            {/* Speakers Field */}
            <div className="mb-6">
              <SpeakerField
                speakers={speakers}
                onSpeakersChange={setSpeakers}
                onSpeakersUpdate={(updatedSpeakers) => {
                  setFormData(prev => ({ ...prev, speakers: updatedSpeakers }));
                }}
              />
            </div>
          </div>

          {/* Video Upload Section */}
          <div className="bg-white rounded-lg p-6 border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CloudArrowUpIcon className="h-5 w-5 mr-2 text-blue-600" />
              Video Upload
            </h3>
            <p className="text-gray-600 mb-6">Upload your video file. You can continue filling out the form while it uploads.</p>
            
            {/* File Upload Section */}
            {!file && !uploading && (
              <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300">
                <FileUpload
                  onFileUpload={handleFileUpload}
                  disabled={file !== null || uploading}
                />
              </div>
            )}
            
            {/* File Status Display */}
            {file && (
              <div className={`mt-4 rounded-lg p-4 border transition-all duration-200 ${
                isFileUploaded && uploadedFileId 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {isFileUploaded && uploadedFileId ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                    ) : (
                      <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-3" />
                    )}
                    <div>
                      <span className="text-sm font-medium text-gray-900">{file.name}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                      {isFileUploaded && uploadedFileId && (
                        <div className="mt-1">
                          <p className="text-xs text-green-600">✓ Uploaded successfully</p>
                          <p className="text-xs text-green-500">File ID: {uploadedFileId}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      console.log('X button clicked, uploadedFileId:', uploadedFileId);
                      if (uploadedFileId) {
                        // If file was uploaded to server, show delete confirmation
                        console.log('Showing delete modal');
                        setShowDeleteModal(true);
                      } else {
                        // If file was only selected locally, just reset state
                        console.log('Resetting local state only');
                        setFile(null);
                        setUploadedFileId(null);
                        setIsFileUploaded(false);
                        resetUpload();
                      }
                    }}
                    className={`p-2 rounded-lg transition-colors ${
                      isFileUploaded && uploadedFileId
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-gray-400 hover:text-red-600'
                    }`}
                    title={uploadedFileId ? "Delete uploaded file" : "Remove selected file"}
                  >
                    {isFileUploaded && uploadedFileId ? (
                      <TrashIcon className="h-4 w-4" />
                    ) : (
                      <XMarkIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploading && progress > 0 && (
              <div className="mt-4 bg-white rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {progress >= 100 ? 'Finalizing upload...' : 'Uploading...'}
                  </span>
                  <span className="text-sm text-gray-500">{Math.min(progress, 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(progress, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Upload Error Display */}
            {uploadError && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <XMarkIcon className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{uploadError}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button and Auto-generate Checkbox */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoGenerateTranscript"
                  checked={formData.auto_generate_transcript}
                  onChange={(e) => handleInputChange('auto_generate_transcript', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="autoGenerateTranscript" className="ml-2 text-sm text-gray-700">
                  Auto-generate transcript
                </label>
              </div>

              {/* Provider Selection Dropdown */}
              {formData.auto_generate_transcript && (
                <div className="flex items-center gap-2">
                  <label htmlFor="transcriptProvider" className="text-sm text-gray-700 font-medium">
                    Provider:
                  </label>
                  <select
                    id="transcriptProvider"
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                  >
                    {TRANSCRIPT_PROVIDERS.map((provider) => (
                      <option key={provider.value} value={provider.value}>
                        {provider.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!canSubmit}
                className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-lg transition-all duration-200"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Instructions when no source is selected */}
      {!selectedSource && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-4">
            <AcademicCapIcon className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-medium text-blue-800 mb-2">Select Content Source</h3>
          <p className="text-blue-700">
            Please select a content source above to begin. You can choose between WordPress events, Faicon events, or create a custom entry.
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          console.log('Delete modal closed');
          setShowDeleteModal(false);
        }}
        title="Delete File"
        size="sm"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <TrashIcon className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Delete Uploaded File?
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            This action cannot be undone. The file will be permanently deleted from the server.
          </p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                console.log('Delete button in modal clicked');
                handleDeleteFile();
              }}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? 'Deleting...' : 'Delete File'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Upload Another File */}
      {createdSessionId && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={resetState}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Upload Another File
          </button>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
          duration={5000}
        />
      )}
    </div>
  );
}